import { useEffect, useRef } from 'react';
import { Modal as ResponsiveModal } from 'react-responsive-modal';
import cn from 'classnames';
// UI
import Text from 'ui3/Text/Text';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Button, { ButtonProps } from 'ui3/Button/Button';
// Styles
import 'react-responsive-modal/styles.css';
import styles from './FullScreenStepperModal.module.scss';

export type FullScreenStepperModalFooter = {
  stepNumber?: number;
  totalStepsNumber?: number;
  leftSection?: JSX.Element;
  showPrevButton?: boolean;
  prevButtonText?: string;
  prevButtonProps?: ButtonProps;
  secondaryButtonText?: string;
  secondaryButtonProps?: ButtonProps;
  submitButtonText?: string;
  submitButtonProps?: ButtonProps;
};

type FullScreenStepperModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  footer?: FullScreenStepperModalFooter;
  children?: JSX.Element;
  closable?: boolean;
  showScrollbar?: boolean;
  /** When true, body scroll is disabled (e.g. when a nested modal is open). */
  bodyScrollLocked?: boolean;
  /** When true, footer is hidden (e.g. when a nested modal is open). */
  hideFooter?: boolean;
  merchChrome?: boolean;
};

const FullScreenStepperModal = ({
  open,
  title,
  onClose,
  footer,
  children,
  closable = true,
  showScrollbar = false,
  bodyScrollLocked = false,
  hideFooter = false,
  merchChrome = false,
}: FullScreenStepperModalProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const stepNumber = footer?.stepNumber;

  // A new step starts at its top, not where the previous one was scrolled to.
  useEffect(() => {
    if (merchChrome) {
      const body = bodyRef.current;

      if (!body) {
        return;
      }

      // scrollIntoView pans the visual viewport sideways on iOS.
      body.scrollTop = 0;
      body.scrollLeft = 0;

      return;
    }

    scrollRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [title, merchChrome, stepNumber]);

  const {
    leftSection,
    showPrevButton,
    prevButtonText,
    prevButtonProps,
    submitButtonText,
    submitButtonProps,
  } = footer || {};

  return (
    <ResponsiveModal
      center={!merchChrome}
      open={open}
      blockScroll={!merchChrome}
      classNames={{
        root: cn(styles.root, { [styles.merchChrome]: merchChrome }),
        overlay: cn(styles.overlay, { [styles.merchChrome]: merchChrome }),
        modal: cn(styles.modal, { [styles.merchChrome]: merchChrome }),
        modalContainer: cn(styles.modalContainer, {
          [styles.showScrollbar]: showScrollbar,
          [styles.merchChrome]: merchChrome,
        }),
      }}
      onClose={onClose}
      showCloseIcon={false}
      // An open date picker or dropdown handles Escape itself (and prevents its default),
      // so only an unhandled Escape closes the whole wizard.
      closeOnEsc={false}
      onEscKeyDown={(event) => {
        if (closable && !event.defaultPrevented) {
          onClose();
        }
      }}
      closeOnOverlayClick={closable}
    >
      <div className={cn(styles.header, { [styles.merchChrome]: merchChrome })}>
        <div
          className={cn(styles.headerBody, {
            [styles.merchChrome]: merchChrome,
          })}
        >
          <Text variant="h5" uppercase>
            {title}
          </Text>
          {closable && (
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close"
            >
              <MillionsIcon
                name={merchChrome ? 'merchOutlineClose' : 'close'}
                size={merchChrome ? 32 : 24}
                className={styles.closeIcon}
              />
            </button>
          )}
        </div>
      </div>
      {/* Used to scroll to the very top of the modal */}
      <div
        className={styles.scrollRef}
        data-testid="scrollRef"
        ref={scrollRef}
      ></div>

      <div
        ref={bodyRef}
        className={cn(styles.body, {
          [styles.withFooter]: Boolean(footer) && !hideFooter,
          [styles.bodyScrollLocked]: bodyScrollLocked,
          [styles.merchChrome]: merchChrome,
        })}
      >
        {children}
      </div>

      {footer && !hideFooter && (
        <div className={styles.footer}>
          {showPrevButton ? (
            <Button
              color="transparent"
              className={styles.prevButton}
              {...prevButtonProps}
            >
              {prevButtonText || 'Back'}
            </Button>
          ) : leftSection ? (
            leftSection
          ) : (
            <div className={styles.prevButtonPlaceholder} />
          )}

          {footer.totalStepsNumber && (
            <div className={styles.stepContainer}>
              <Text variant="body1Regular16" color="lights-medium">
                {footer.stepNumber} of {footer.totalStepsNumber} steps
              </Text>
            </div>
          )}

          <div className={styles.buttonsContainer}>
            {footer.secondaryButtonText && (
              <Button
                color="black"
                className={styles.secondaryButton}
                {...footer.secondaryButtonProps}
              >
                {footer.secondaryButtonText}
              </Button>
            )}
            <Button
              color="black"
              className={styles.submitButton}
              {...submitButtonProps}
            >
              {submitButtonText || 'Next'}
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
};

export default FullScreenStepperModal;
